import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  SimpleGrid,
  Text,
  useColorModeValue,
  useToast,
  VStack,
} from '@chakra-ui/react'
import { ArrowLeft, Plus as PlusIcon, Search, Sparkles, Star, X } from 'lucide-react'
import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import AgentCard from '../components/cards/AgentCard'
import CreateAgentDialog from '../components/CreateAgentDialog'
import { useUser } from '../hooks/auth/useUser'

import { useAgents } from '../hooks/useAgents'
import { useDepartments } from '../hooks/agents/useDepartments'
import { useLanguage } from '../hooks/utility/useLanguage'

import { ErrorState, LoadingState } from '../global/components'
import useMobile from '../hooks/utility/useMobile'
import { Agent, JobCategory, Tag } from '../types/agentTypes'
import { getIconByName } from '../utils/iconUtils'



const LibraryPage: React.FC = () => {
  const navigate = useNavigate()
  const { t } = useLanguage()
  const isMobile = useMobile()
  const toast = useToast()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedDepartment, setSelectedDepartment] = useState<string | null>(null)
  const [selectedJobCategory, setSelectedJobCategory] = useState<string | null>(null)
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false)
  const [showCreateAgent, setShowCreateAgent] = useState(false)
  const { data: user } = useUser()

  // Color mode values
  const bgColor = useColorModeValue('gray.50', 'gray.900')
  const cardBg = useColorModeValue('white', 'gray.800')
  const textMuted = useColorModeValue('gray.500', 'gray.400')
  const emptyStateIconColor = useColorModeValue('gray.400', 'gray.500')

  const { agents, isLoadingAgents, agentError, toggleAgentFavorite } = useAgents()
  const { departments } = useDepartments()

  // Calculate favorite count
  const favoriteCount = useMemo(() => {
    if (!agents) return 0
    return agents.filter(agent => agent.is_preferred && agent.type !== 'privileged').length
  }, [agents])

  // Get unique job categories for the selected department
  const jobCategoriesForDepartment = useMemo(() => {
    if (!agents || !selectedDepartment) return []

    const departmentAgents = agents.filter(
      agent => agent.department?.id === selectedDepartment && agent.type !== 'privileged',
    )

    const jobCategoriesMap = new Map<string, JobCategory>()

    departmentAgents.forEach(agent => {
      if (agent.job_category) {
        jobCategoriesMap.set(agent.job_category.id, agent.job_category)
      }
    })

    return Array.from(jobCategoriesMap.values()).sort((a, b) => a.name.localeCompare(b.name))
  }, [agents, selectedDepartment])

  // Filter agents based on search query, department, job category, and favorites
  const filteredAgents = useMemo(() => {
    if (!agents) return []

    let filtered = [...agents]

    // Filter out privileged agents (they live in the sidebar, not the library)
    filtered = filtered.filter(agent => agent.type !== 'privileged')

    // Filter by favorites if selected
    if (showFavoritesOnly) {
      filtered = filtered.filter(agent => agent.is_preferred)
    }

    // Filter by department if selected
    if (selectedDepartment) {
      filtered = filtered.filter(agent => agent.department?.id === selectedDepartment)
    }

    // Filter by job category if selected
    if (selectedJobCategory) {
      filtered = filtered.filter(agent => agent.job_category?.id === selectedJobCategory)
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(
        agent =>
          agent.name.toLowerCase().includes(query) ||
          (agent.description && agent.description.toLowerCase().includes(query)) ||
          agent.tags.some((tag: Tag) => tag.name.toLowerCase().includes(query)),
      )
    }

    // Sort alphabetically by name
    return filtered.sort((a, b) => a.name.localeCompare(b.name))
  }, [agents, searchQuery, selectedDepartment, selectedJobCategory, showFavoritesOnly])

  // Calculate filter stats directly (no state needed)
  const filterStats = useMemo(() => {
    if (!agents) return { total: 0, displayed: 0 }

    const nonPrivilegedTotal = agents.filter(agent => agent.type !== 'privileged').length
    return {
      total: nonPrivilegedTotal,
      displayed: filteredAgents.length,
    }
  }, [agents, filteredAgents])

  const handleAgentSelect = (agent: Agent) => {
    navigate(`/a/${agent.id}`)
    toast({
      title: t('library.agentSelected'),
      description: `${agent.name} ${t('library.isNowActive')}`,
      status: 'success',
      duration: 2000,
      isClosable: true,
    })
  }

  const handleEditAgent = (agent: Agent) => {
    navigate(`/library/a/${agent.id}`)
  }

  const handleToggleFavorite = (agentId: string) => {
    toggleAgentFavorite(agentId)
  }

  const handleCreateAgent = () => {
    setShowCreateAgent(true)
  }

  const handleCreateAgentClose = () => {
    setShowCreateAgent(false)
  }

  const handleBack = () => {
    navigate('/')
  }

  const clearSearchQuery = () => {
    setSearchQuery('')
  }

  const handleDepartmentSelect = (departmentId: string | null) => {
    setSelectedDepartment(departmentId)
    setSelectedJobCategory(null) // Clear job category when switching departments
    setShowFavoritesOnly(false)
  }

  const handleJobCategorySelect = (jobCategoryId: string | null) => {
    setSelectedJobCategory(jobCategoryId)
  }

  if (isLoadingAgents) {
    return (
      <LoadingState
        message={t('common.loading')}
        useContainer
        variant="spinner"
        size="lg"
      />
    )
  }

  if (agentError) {
    return (
      <ErrorState
        message={agentError}
        useContainer
        onAction={() => window.location.reload()}
      />
    )
  }

  return (
    <Box minH='100vh' bg={bgColor}>
      <Container maxW='container.xl' py={8}>
        <VStack spacing={8} align='stretch'>
          {/* Header with Back Button, Title, and Create Button */}
          <Flex justify='space-between' align='center'>
            <IconButton
              icon={<ArrowLeft size={24} />}
              aria-label={t('common.back')}
              variant='ghost'
              size='lg'
              color='primary'
              onClick={handleBack}
              _hover={{ bg: 'primaryScheme.50' }}
            />
            <Heading size={isMobile ? 'lg' : 'xl'} textAlign='center' flex='1' color='primary'>
              {t('library.title')}
            </Heading>
            {user?.is_superuser && (
              <Button
                leftIcon={<PlusIcon size={20} />}
                onClick={handleCreateAgent}
                variant='solid'
                colorScheme='primaryScheme'
                size='lg'
                flexShrink={0}
              >
                {t('library.createAgent')}
              </Button>
            )}
          </Flex>

          {/* Search and Stats */}
          <Box>
            <InputGroup mb={2}>
              <InputLeftElement pointerEvents='none'>
                <Search size={20} color='var(--chakra-colors-primary)' />
              </InputLeftElement>
              <Input
                placeholder={t('library.searchPlaceholder')}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                borderRadius='lg'
                focusBorderColor='primary'
                bg={cardBg}
              />
              {searchQuery && (
                <InputRightElement>
                  <IconButton
                    icon={<X size={16} />}
                    aria-label='Clear search'
                    size='sm'
                    variant='ghost'
                    onClick={clearSearchQuery}
                  />
                </InputRightElement>
              )}
            </InputGroup>
            <Flex justify='space-between' align='center' px={1}>
              <Text fontSize='sm' color={textMuted}>
                {filterStats.displayed} {t('library.of')} {filterStats.total} {t('library.assistants')}
              </Text>
            </Flex>
          </Box>

          {/* Filter Tabs */}
          <Box>
            <Flex gap={2} mb={4} flexWrap='wrap' align='flex-start'>
              {/* Show subcategories when department is selected, otherwise show main buttons */}
              {selectedDepartment && jobCategoriesForDepartment.length > 0 ? (
                <>
                  {/* Back to department selection */}
                  <Button
                    size='sm'
                    variant='outline'
                    colorScheme='gray'
                    onClick={() => {
                      setSelectedDepartment(null)
                      setSelectedJobCategory(null)
                    }}
                    leftIcon={<ArrowLeft size={14} />}
                    iconSpacing={1}
                    px={3}
                  >
                    {t('common.back')}
                  </Button>

                  {/* Department name indicator with hierarchy styling */}
                  <Button
                    size='sm'
                    variant={!selectedJobCategory ? 'solid' : 'outline'}
                    colorScheme={!selectedJobCategory ? 'primaryScheme' : 'primaryScheme'}
                    onClick={() => handleJobCategorySelect(null)}
                    px={3}
                    fontWeight='semibold'
                  >
                    {departments.find(d => d.id === selectedDepartment)?.name}
                  </Button>

                  {/* Hierarchy separator */}
                  <Text
                    fontSize='sm'
                    color='gray.400'
                    alignSelf='center'
                    px={1}
                  >
                    →
                  </Text>

                  {/* Job category buttons */}
                  {jobCategoriesForDepartment.map(jobCategory => {
                    const jobIcon = getIconByName(jobCategory.icon_url || null);
                    return (
                      <Button
                        key={jobCategory.id}
                        size='sm'
                        variant={selectedJobCategory === jobCategory.id ? 'solid' : 'ghost'}
                        colorScheme={selectedJobCategory === jobCategory.id ? 'primaryScheme' : 'gray'}
                        onClick={() => handleJobCategorySelect(jobCategory.id)}
                        leftIcon={jobIcon || undefined}
                        px={3}
                        iconSpacing={1}
                      >
                        {jobCategory.name}
                      </Button>
                    );
                  })}
                </>
              ) : (
                <>
                  {/* Main filter buttons */}
                  <Button
                    size='sm'
                    variant={showFavoritesOnly ? 'solid' : 'outline'}
                    colorScheme={showFavoritesOnly ? 'primaryScheme' : 'gray'}
                    onClick={() => {
                      setShowFavoritesOnly(true)
                      setSelectedDepartment(null)
                      setSelectedJobCategory(null)
                    }}
                    leftIcon={<Star size={14} />}
                    iconSpacing={1}
                    px={3}
                  >
                    {t('library.favorites')} ({favoriteCount})
                  </Button>
                  <Button
                    size='sm'
                    variant={!selectedDepartment && !showFavoritesOnly ? 'solid' : 'outline'}
                    colorScheme={!selectedDepartment && !showFavoritesOnly ? 'primaryScheme' : 'gray'}
                    onClick={() => handleDepartmentSelect(null)}
                    px={3}
                  >
                    {t('library.all')}
                  </Button>

                  {/* Department buttons */}
                  {departments.map(department => {
                    const icon = getIconByName(department.icon_url || null);
                    return (
                      <Button
                        key={department.id}
                        size='sm'
                        variant='outline'
                        colorScheme='gray'
                        onClick={() => handleDepartmentSelect(department.id)}
                        leftIcon={icon || undefined}
                        px={3}
                        iconSpacing={1}
                      >
                        {department.name}
                      </Button>
                    );
                  })}
                </>
              )}
            </Flex>
          </Box>

          {/* Agents Grid */}
          {filteredAgents.length === 0 ? (
            <Flex justify='center' align='center' minH='300px'>
              <VStack spacing={4}>
                <Sparkles size={48} color={emptyStateIconColor} />
                <Text color={textMuted} fontSize='lg' textAlign='center'>
                  {searchQuery || selectedDepartment || selectedJobCategory || showFavoritesOnly
                    ? t('library.noSearchResults')
                    : t('library.noAgents')}
                </Text>
                {!searchQuery && !selectedDepartment && !selectedJobCategory && !showFavoritesOnly && user?.is_superuser && (
                  <Button
                    leftIcon={<PlusIcon size={16} />}
                    colorScheme='primaryScheme'
                    variant='outline'
                    onClick={handleCreateAgent}
                  >
                    {t('library.createFirstAgent')}
                  </Button>
                )}
              </VStack>
            </Flex>
          ) : (
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} spacing={6}>
              {filteredAgents.map(agent => (
                <AgentCard
                  key={agent.id}
                  agent={agent}
                  isSelected={false}
                  isSuperuser={!!user?.is_superuser}
                  onSelect={handleAgentSelect}
                  onEdit={handleEditAgent}
                  onToggleFavorite={handleToggleFavorite}
                />
              ))}
            </SimpleGrid>
          )}
        </VStack>
      </Container>

      {/* Create Agent Dialog */}
      <CreateAgentDialog
        isOpen={showCreateAgent}
        onClose={handleCreateAgentClose}
      />
    </Box>
  )
}

export default LibraryPage
